export const irrigationEn = {
  dualKcOnboarding: {
    title: "Water evidence setup (Dual Kc)",
    subtitle: "Supervised. You record what really happened in the field; nothing is assumed for you.",
    disclaimer: "Declared values are labelled as declared, never as measured. Missing information stays unknown and the FAO-56 Dual Kc balance blocks instead of guessing.",
    saved: "Evidence recorded",
    readyNote: "All required evidence is present. The next scheduled Dual Kc run can proceed on its own.",
    currentChainComplete: "Current Dual Kc evidence chain complete for this calculation date.",
    oneTime: "One-time setup",
    ongoing: "Ongoing (daily)",
    status: {
      ready: "Ready",
      needs_initialization: "Initialization needed",
      needs_irrigation_evidence: "Irrigation evidence needed",
      blocked: "Blocked",
      invalid: "Invalid",
    },
    notesPlaceholder: "Optional note (max 500 characters)",
    unit: {
      mm: "mm",
      m3_ha: "m³/ha",
      l_ha: "l/ha",
      m3_total: "m³ (total)",
      l_total: "litres (total)",
    },
    error: {
      generic: "Could not record this evidence.",
      not_authenticated: "Please sign in again.",
      invalid_input: "Please check the value entered.",
      taw_unknown: "Soil water capacity (TAW) is unknown for this parcel, so a depletion in mm cannot be validated yet.",
      dr_above_taw: "The declared depletion cannot exceed the total available water of the root zone.",
      amount_not_convertible: "This amount cannot be converted to mm with the information provided.",
      irrigation_system_unknown: "Document the parcel irrigation system before declaring a wetted soil fraction.",
      fw_out_of_range: "Enter a wetted soil surface between 1% and 100%.",
    },
    input: {
      crop_identification: {
        question: "Which crop is growing on this parcel?",
        why: "The crop determines root depth, Kc and the depletion fraction.",
        short: "Crop identified",
        action: "Set the crop type on the parcel record to continue.",
      },
      soil_water_capacity: {
        question: "Soil water capacity is not available yet",
        why: "Total available water (TAW) is required before any depletion can be interpreted.",
        short: "Soil water capacity",
        action: "Add a soil analysis or wait for the soil evidence pipeline to resolve this parcel.",
      },
      weather_evidence: {
        question: "Weather evidence is not available for this date",
        why: "ET₀ must exist before the crop water balance can run.",
        short: "Weather evidence",
        action: "No provider-backed weather is recorded for this date yet.",
      },
      surface_wetting_confirmation: {
        question: "Was the soil surface fully wetted today (irrigation or heavy rain)?",
        why: "This is the only approved way to start the surface evaporation state (De).",
        short: "Surface wetting confirmed",
        confirm: "Yes, the surface was fully wetted today",
      },
      irrigation_system_evidence: {
        question: "Which irrigation system does this parcel use?",
        why: "The irrigation system is evidence. While it is unknown, no wetted soil fraction may be entered or assumed.",
        short: "Irrigation system documented",
        helper: "Confirm or update the irrigation system on the parcel record. An unknown system is never read as sprinkler.",
        configure: "Open parcel settings",
      },
      wetted_fraction_evidence: {
        question: "What fraction of the soil surface is actually wetted by irrigation?",
        why: "FAO-56 needs the wetted soil fraction (fw). It is never derived from the irrigation system type.",
        short: "Wetted soil fraction",
        label: "Wetted soil surface (%)",
        helper: "Enter a value only if you know it from field observation, irrigation-system documentation or a measured/verified source. If you do not know it, do not estimate it: leaving it unknown keeps the dual Kc calculation blocked.",
        declare: "Save declared wetted fraction",
        unknown: "I don't know",
        unknownToast: "Nothing was saved. The wetted soil fraction stays unknown and the dual Kc calculation remains blocked.",
        declaredNote: "Producer declared — limited-quality evidence, not measured and not valid for independent validation.",
      },
      root_zone_initialization: {
        question: "How much water is missing from the root zone today?",
        why: "The root-zone depletion (Dr) needs one real starting point; it is never assumed.",
        short: "Root zone initialized",
        drLabel: "Root-zone depletion (mm)",
        declare: "Record declared depletion",
        fieldCapacity: "The root zone was fully refilled today",
        declaredNote: "A declared depletion is recorded as declared evidence with limited quality, never as a measurement.",
      },
      irrigation_evidence: {
        question: "Was irrigation applied to this parcel on the date below?",
        why: "Occurrence and amount are separate facts; an unknown amount is never treated as 0 mm.",
        short: "Daily irrigation evidence",
        dateLabel: "Date: {{date}}",
        amount: "Amount applied",
        system: "Irrigation system (optional)",
        applied: "Record irrigation applied",
        none: "No irrigation applied",
        amountUnknown: "I don't know the amount",
        enterAmount: "Enter the amount",
        uncertain: "Not sure",
        unknownNote: "If the amount is unknown, the balance blocks for that day instead of assuming a depth.",
      },
    },
  },
  irrigationPage: {
    title: "Irrigation Manager",
    subtitle: "Optimize water usage with scientific ET0 calculations"
  },
  irrigationOptimizer: {
    title: "Irrigation Optimizer",
    premiumFeature: "Irrigation optimization is a premium feature. Upgrade to access AI-powered water management.",
    upgradeToAccess: "Upgrade to Access",
    fao56Method: "FAO-56 Calculation Method",
    fao56MethodDesc: "Choose between Single Kc reference demand and evidence-gated Dual Kc water balance.",
    method: "Method",
    kcSourceBadge: "Kc: FAO-56 Table 12 reference values (Allen et al. 1998) — reference source, not field-validated",
    et0ConcordanceBadge: "ET₀ Penman-Monteith: model-vs-model concordance check only — no independent field validation",
    singleKcValidated: "Single Kc — reference demand",
    dualKcBeta: "Dual Kc 🧪 Beta",
    dualKcValidated: "Dual Kc — implemented (evidence-gated)",
    singleKcDesc: "FAO-56 tabulated crop-coefficient reference method. Provides reference crop water demand when the required crop, phenology and ETo evidence is available; it is not field-validated irrigation demand.",
    dualKcDesc: "FAO-56 Dual Kc water-balance method. Implementation is evidence-gated; independent field validation is pending.",
    fieldValidationPending: "Field Validation Pending",
    refresh: "Refresh",
    aiPoweredDesc: "AI-powered water management recommendations for your parcels",
    weatherDataSources: "Weather Data Sources",
    weatherDataSourcesDesc: "Weather evidence is selected per meteorological variable from the approved provider chain, so a single calculation may combine providers. The provider actually used for each variable is shown in the evidence details.",
    operationalGrid: "Operational Grid",
    operationalGridDesc: "Decisions calculated on ~55×55m cells (NDVI base at 10m; aggregated for robustness and management).",
    expectedAccuracy: "Evidence status",
    expectedAccuracyDesc: "ET₀ has an operational model-vs-model concordance check. Single Kc and Dual Kc irrigation outputs do not yet have independent field-validation accuracy metrics.",
    optimalRange: "Optimal Range",
    optimalRangeDesc: "2-20 ha for best precision. Parcels <1 ha: recommend supplementing with local sensors.",
    criticalNote: "For critical irrigation decisions, validate with agronomist and field sensors.",
    analyzingNeeds: "Analyzing irrigation needs...",
    noParcelsFound: "No parcels found. Add a parcel to get started.",
    noParcelsToast: "No parcels found",
    noParcelsToastDesc: "Please add parcels first to generate irrigation plans",
    plansCalculated: "{{method}} irrigation plans calculated",
    analysisComplete: "{{method}} parcel analysis complete",
    analysisCompleteDesc: "{{success}} calculated · {{blocked}} blocked",
    analysisCompleteDescWithErrors: "{{success}} calculated · {{blocked}} blocked · {{errors}} errors",
    plansCalculatedDesc: "Analyzed {{count}} parcel(s) using FAO-56 {{methodFull}} method",
    errorLoadingPlans: "Error loading irrigation plans",
    selectParcel: "Select a parcel",
    allParcels: "All parcels",
    parcelCount: "{{count}} parcel(s)",
    blockedInvalidEtc: "Irrigation calculation blocked — invalid ETc. Review crop cycle, planting/harvest date or FAO-56 parameters.",
    forecastUnavailable: "Forecast not available — adjustment not computed.",
    adjHighDemand: "High atmospheric demand (elevated ET₀ / heat)",
    adjElevatedDemand: "Elevated atmospheric demand (ET₀ / temperature)",
    adjNormalDemand: "Atmospheric demand within normal range"
  },
  irrigationPlan: {
    waterStressLevel: "Water Stress Level",
    adjustedFor: "adjusted for {{season}}",
    requiresSatelliteData: "Requires satellite data (NDVI/NDWI)",
    highStress: "High",
    moderateStress: "Moderate",
    lowStress: "Low",
    stress: "Stress",
    temperature: "Temperature",
    humidity: "Humidity",
    frequency: "Frequency",
    asNeeded: "As needed",
    next3DaysAdjustments: "Next 3 Days Adjustments",
    weekly: "Weekly",
    monthly: "Monthly",
    yearly: "Yearly",
    satelliteStressIndicator: "Satellite/climate stress indicator",
    satelliteStressIndicatorHelper: "Independent indicator derived from Sentinel-2 NDVI/NDWI against seasonal crop thresholds (0.6 x NDVI stress + 0.4 x NDWI stress). It is not the FAO-56 root-zone stress coefficient Ks and does not describe current soil water depletion.",
    estimatedCropWaterDemand: "Estimated crop water demand",
    estimatedCropWaterDemandHelper: "Reference estimate only (ETc = Kc x ET₀, Single Kc). Not an instruction to apply this volume: the current soil-water balance is unavailable.",
    vriReferenceHelper: "Zones express relative NDVI vigour only. No per-zone irrigation depth or volume is prescribed.",
    canonicalBalanceUnavailableTitle: "Current soil-water balance unavailable",
    canonicalBalanceUnavailableBody: "The evidence-gated FAO-56 Dual Kc water balance has no valid result for today, so no supervised irrigation instruction can be given. What follows is a reference crop water demand estimate only.",
    dualKcDemandLine: "Canonical Dual Kc water balance: ETc ≈ {{mm}} mm/day — review in the field before irrigating",
    noSavingsClaimTitle: "No water or cost saving is claimed",
    noSavingsClaimBody: "Savings require a measured irrigation baseline for this parcel. None exists, so no modelled €/litre saving is shown.",
    zoneRelativeVigour: "Relative NDVI vigour class — no irrigation depth is prescribed per zone",
    referenceDemandLine: "Reference crop water demand: ~{{mm}} mm/day (Single Kc estimate, not a current soil-water balance)",
    vriZones: "Variable Rate Irrigation (VRI) Zones",
    vriZonesUnavailableTitle: "VRI zones unavailable",
    vriZonesUnavailableBody: "Variable Rate Irrigation zones could not be calculated for this parcel right now (no usable cloud-free NDVI zonification). The parcel boundary is recorded and remains valid.",
    vriBoundaryRequiredTitle: "Parcel boundary required",
    vriBoundaryRequiredBody: "Real polygon coordinates are required for NDVI-based zonification. Draw your parcel boundaries on the map to enable Variable Rate Irrigation zones.",
    faoBlockedTitleDual: "Dual Kc water balance unavailable",
    faoBlockedTitleSingle: "Single Kc calculation unavailable",
    faoBlockedStatus: "Status",
    faoBlockedSafeNextStep: "Safe next step",
    faoBlockedDate: "Calculation date",
    faoBlockedIndependentNote: "This blocks only the FAO-56 water balance. Satellite NDVI/NDWI, the stress indicator, parcel geometry and VRI zones are independent evidence and remain valid where shown.",
    faoErrorTitle: "Calculation error",
    zones: "{{count}} zones",
    scientificMethod: "Scientific Method",
    recommendations: "Recommendations",
    certifyAgronomicData: "Certify Agronomic Data",
    blockchainProof: "Tamper-evident proof of authenticity (Early Access)",
    insuranceClaims: "Insurance claims",
    regulatoryAudits: "Regulatory audits",
    premiumTraceability: "Premium traceability",
    needProfessionalGuidance: "Need Professional Guidance?",
    scheduleConsultationDesc: "Schedule a consultation with certified agronomists to validate irrigation plans and optimize your water management strategy.",
    scheduleConsultation: "Schedule Agronomist Consultation",
    dualKcBreakdownTitle: "Dual Kc method (FAO-56 Ch. 7) — Kcb, Ke and Ks breakdown",
    cropEvapotranspirationLabel: "Crop evapotranspiration (ETc)",
    referenceDemandParcelEquivalent: "Parcel-scale equivalent of this demand: ~{{liters}} L/day. This is an equivalence of modelled crop water use, not an amount to apply.",
    demandTrendHigher: "Higher atmospheric demand",
    demandTrendLower: "Lower atmospheric demand",
    demandTrendStable: "Stable atmospheric demand",
    actionsBlockedTitle: "Saving and field-notebook registration are unavailable",
    actionsBlockedBody: "These figures are modelled crop water demand, not a reviewed irrigation application target. Saving them as an irrigation plan, or recording them as water applied in the field notebook, would misrepresent what actually happened in the field.",
    basalTranspiration: "Basal Transpiration",
    soilEvaporation: "Soil Evaporation",
    waterStressFactor: "Water Stress Factor",
    perHaPerDay: "mm/day per ha",
    estimatedCropWaterDemandHelperDual: "Crop evapotranspiration (ETc) computed by the FAO-56 Dual Kc balance. It is how much water the crop is modelled to use, not how much to apply.",
    blockedTitle: "Irrigation plan blocked",
    blockedInvalidEtc: "Invalid FAO-56 output (ETc / Kc / ET₀). Review crop cycle, planting/harvest date and FAO-56 parameters.",
    lifecycleReviewRequired: "Crop cycle exceeded — confirm harvest status or current crop cycle.",
    satLowButAtmHigh: "Low satellite water stress, but elevated atmospheric demand.",
    satHighAndAtmHigh: "High satellite water stress and elevated atmospheric demand."
  },
  soilWaterBalance: {
    title: "Soil Water Balance (FAO-56 Ch. 8)",
    subtitle: "Simplified AquaCrop model: field capacity, wilting point and rainfall recharge",
    soilType: "Soil type",
    period: "Period",
    calculate: "Calculate",
    sand: "Sand",
    sandyLoam: "Sandy loam",
    loam: "Loam",
    siltLoam: "Silt loam",
    clayLoam: "Clay loam",
    clay: "Clay",
    days14: "14 days",
    days30: "30 days",
    days60: "60 days",
    currentMoisture: "Current moisture",
    stressCoefficient: "Stress coeff. (Ks)",
    daysToStress: "Days to stress",
    now: "Now",
    irrigationNeeded: "Irrigation needed",
    toReplenishRAW: "to replenish RAW",
    noStress: "No stress",
    mildStress: "Mild stress",
    moderateStress: "Moderate stress",
    severeStress: "Severe stress",
    soilMoisture: "Soil moisture (mm)",
    precipitation: "Precipitation (mm)",
    adjustedETc: "Adjusted ETc (mm)",
    irrigation: "Irrigation (mm)",
    waterStressCoefficient: "Water Stress Coefficient (Ks)",
    fao56Chapter8Model: "FAO-56 Chapter 8 Model",
    modelledReferenceBadge: "Modelled reference",
    modelledReferenceNotice: "Modelled reference — not canonical water-balance evidence. Values come from a daily model with declared assumptions and require human review.",
    tawProvenanceLabel: "Soil-water capacity (TAW) source",
    tawProvenanceUnknown: "No canonical provenance recorded",
    soil: "Soil",
    stressDays: "Stress days",
    of: "of",
    totalETc: "Total ETc",
    totalPrecipitation: "Precipitation"
  },
  recommendationActions: {
    saved: "Saved ✓",
    saveRecommendation: "Save Recommendation",
    registeredInNotebook: "Registered in Notebook ✓",
    registerInNotebook: "Register in Field Notebook",
    saveFirst: "Save first to register in notebook"
  },
  weatherProvenance: {
    title: "Weather evidence",
    mixed: "Mixed sources",
    fallback: "Fallback source used",
    fallbackShort: "Fallback",
    limited: "Limited weather inputs",
    modelled: "Modelled / reanalysis",
    notAvailable: "Not available",
    showDetails: "Show source per measurement",
    hideDetails: "Hide source details",
    unknown: {
      title: "Weather source not recorded",
      body: "This calculation predates variable-level weather provenance, so the source of each measurement is unknown.",
    },
    variable: {
      tmax: "Maximum temperature",
      tmin: "Minimum temperature",
      tmean: "Mean temperature",
      humidity: "Relative humidity",
      humidityMin: "Minimum relative humidity (RHmin)",
      wind: "Wind speed (2 m)",
      radiation: "Solar radiation",
      elevation: "Elevation",
      precipitation: "Precipitation",
    },
  },
  et0Plausibility: {
    title: "Reference evapotranspiration (ETo)",
    raw: "Calculated ETo",
    effective: "Used downstream",
    notUsed: "Not used",
    capApplied: "Explicit cap applied",
    reviewNote: "High ETo — review recommended.",
    blockedNote: "Extreme value — not used in Dual Kc pending validation of the weather evidence.",
    reasons: "Context",
    status: {
      plausible: "Plausibility: Normal",
      elevated: "High ETo — review recommended",
      extremeReview: "Extreme value — review required",
      implausible: "Implausible value",
      invalid: "Invalid value",
    },
    unknown: {
      title: "ETo plausibility not recorded",
      body: "This calculation predates the ETo plausibility policy, so raw and effective values were not recorded separately.",
      capUnknown: "This calculation predates the plausibility policy. Whether its value was limited by the previous ceiling cannot be determined, so the original calculated value is unknown.",
    },
  },
  dualKcCoreEvidence: {
    title: "Core input provenance",
    soil: "Soil texture",
    precipitation: "Rainfall",
    phenology: "Crop age",
    days: "{{count}} days since planting",
    parameterNote: "REW/TEW are published model parameters selected by texture, not measured parcel values.",
    unknown: {
      title: "Core input provenance not recorded",
      body: "This calculation predates the core-evidence contract, so the sources of soil texture, rainfall and crop age were not recorded.",
    },
  },
  // FAO56-DUAL-KC-RUNOFF-EFFECTIVE-RAIN-POLICY-1
  // FAO56-DUAL-KC-SOIL-EVAPORATION-STATE-1
  // FAO56-DUAL-KC-CROP-HEIGHT-EVIDENCE-1
  cropHeight: {
    title: "Crop height used for climate adjustment",
    height: "Height",
    source: "Origin",
    evidenceType: "Evidence",
    stage: "Stage",
    range: "Reference range",
    observedAt: "Recorded on",
    policy: "Selection policy",
    notKnown: "not known",
    unknown: {
      title: "Crop height provenance not recorded",
    },
    legacy: {
      legacy_height_provenance_unknown: "This calculation predates the crop-height evidence contract, so it cannot be shown whether the height was measured, declared or a default value.",
      no_height_recorded: "No crop height was recorded for this calculation.",
    },
    missing: {
      title: "Crop height unavailable",
      body: "The climate adjustment could not be calculated because no crop height evidence exists for this parcel. No generic height was assumed.",
    },
    sourceValue: {
      producer_measured: "measured in the field",
      producer_declared: "declared by the producer",
      crop_stage_reference: "FAO-56 crop reference (stage aware)",
      crop_reference: "FAO-56 crop reference",
      none: "no source",
    },
    evidenceTypeValue: {
      measured: "measurement",
      declared: "declaration",
      reference: "reference value",
      modelled: "modelled value",
      none: "none",
    },
    quality: {
      high: "high quality",
      medium: "medium quality",
      limited: "limited confidence",
      unavailable: "unavailable",
    },
    policyValue: {
      producer_value_as_reported: "producer value as reported",
      fao56_table12_single_value: "FAO-56 Table 12 published value",
      fao56_table12_conservative_min: "FAO-56 Table 12 range, conservative lower bound",
    },
    limitation: {
      declared_by_producer_not_instrument_measured: "Declared by the producer, not instrument measured.",
      declaration_date_unknown: "The declaration date is unknown.",
      declaration_older_than_21_days: "The declaration is more than three weeks old.",
      crop_specific_reference_height_not_a_parcel_measurement: "Crop-specific reference height, not a parcel measurement.",
      tabulated_height_is_representative_of_the_mid_season_stage: "The tabulated height represents the mid-season stage.",
      stage_is_early_so_reference_height_likely_overstates_actual_height: "The crop is in an early stage, so the reference height probably overstates the real height.",
      growth_stage_unknown_so_stage_specific_height_could_not_be_applied: "Growth stage unknown, so no stage-specific height was applied.",
      crop_type_has_no_approved_crop_height_reference: "This crop type has no approved crop-height reference.",
      crop_type_is_a_category_not_a_crop_so_no_reference_height_applies: "The crop type is a category, not a crop, so no reference height applies.",
      producer_height_rejected_by_validation: "The stored producer height did not pass validation.",
      no_generic_crop_height_is_assumed: "No generic crop height is assumed.",
    },
  },
  depletionFraction: {
    title: "Depletion fraction (p) provenance",
    p: "p used",
    basePValue: "Table value (ETc ≈ 5 mm/day)",
    source: "Origin",
    policy: "Selection",
    adjustment: "Atmospheric-demand adjustment",
    adjustmentApplied: "applied ({{etc}} mm/day, Δp {{delta}})",
    adjustmentNotApplied: "not applied",
    unknown: {
      title: "Depletion fraction provenance not recorded",
      body: "This calculation predates the depletion-fraction contract, so the origin of the stress threshold was not recorded.",
    },
    status: {
      selected: "crop reference",
      missing: "not available",
      invalid: "not usable",
      blocked: "blocked",
    },
    sourceValue: {
      fao56_crop_reference: "FAO-56 Table 22 crop reference",
      project_reference: "project reference",
      producer_parameter: "producer parameter",
      none: "no source",
    },
    policyValue: {
      fao56_table22_crop_value: "published value for this crop",
      fao56_table22_group_value: "published value for the crop group",
      fao56_table22_conservative_min: "lower bound of the published range",
    },
    quality: {
      high: "high",
      medium: "medium",
      limited: "limited",
      unavailable: "unavailable",
    },
    limitation: {
      tabulated_p_is_a_crop_level_reference_not_a_stage_specific_or_parcel_specific_value:
        "The tabulated p is a crop-level reference, not a growth-stage or parcel-specific value.",
      etc_under_standard_conditions_unavailable_so_the_table22_demand_adjustment_was_not_applied:
        "Crop water demand under standard conditions was unavailable, so the demand adjustment was not applied.",
      adjusted_p_limited_by_the_published_0_1_to_0_8_bounds:
        "The adjusted p was limited by the published 0.1–0.8 bounds.",
      group_level_reference_less_specific_than_a_named_crop_value:
        "A crop-group reference was used because this crop is not listed individually.",
      crop_type_has_no_approved_depletion_fraction_reference:
        "This crop has no approved depletion-fraction reference.",
      crop_type_is_a_category_not_a_crop_so_no_reference_depletion_fraction_applies:
        "The crop type is a category, not a crop, so no reference depletion fraction applies.",
      no_generic_depletion_fraction_is_assumed: "No generic depletion fraction is assumed.",
      resolved_depletion_fraction_outside_the_valid_open_interval_0_1:
        "The resolved depletion fraction fell outside its valid range.",
    },
  },
  soilEvaporationState: {
    title: "Soil surface evaporation state",
    de: "Surface depletion (start of day)",
    capacity: "REW / TEW",
    source: "Origin",
    sourceDate: "Reference date",
    previousEvaporation: "Previous-day evaporation",
    previousInfiltration: "Previous-day water input",
    rejection: "Not available because",
    notKnown: "not known",
    unknown: {
      title: "Surface evaporation state not recorded",
      body: "This calculation predates the surface-layer state contract, so the origin of the soil evaporation state was not recorded.",
    },
    status: {
      propagated: "carried over from yesterday",
      initialized: "initialized from evidence",
      limited_initialization: "declared starting point",
      missing: "not available",
      invalid: "not usable",
      blocked: "blocked",
    },
    sourceValue: {
      previous_dual_kc_state: "previous daily balance",
      confirmed_full_wetting: "confirmed full wetting of the surface",
      none: "no source",
    },
    reason: {
      previous_state_missing: "there is no previous daily surface state and no confirmed wetting event",
      previous_state_invalid: "the previous surface state is incomplete or out of physical bounds",
      previous_state_legacy_untrusted: "the previous record predates the evidence contract",
      tew_incompatible: "the evaporating-layer capacity changed materially",
      temporal_gap: "there is a gap in the daily series",
      previous_state_read_failed: "the previous state could not be read",
      previous_irrigation_unknown: "the irrigation applied yesterday is unknown",
      previous_rain_water_unavailable: "yesterday's rainfall could not be resolved",
      previous_wetted_fraction_unknown: "the wetted soil fraction of yesterday is unknown",
      previous_evaporation_unknown: "yesterday's evaporation was not recorded",
      invalid_tew_or_rew: "the evaporating-layer parameters are not usable",
    },
    quality: {
      high: "high confidence",
      medium: "medium confidence",
      limited: "limited confidence",
      unavailable: "unavailable",
    },
  },
  rainWater: {
    title: "Rainfall entering the balance",
    precipitation: "Rainfall (observed)",
    source: "Provider",
    runoff: "Runoff",
    effective: "Water entering the balance",
    method: "Method",
    notKnown: "not known",
    unknown: {
      title: "Rainfall balance provenance not recorded",
    },
    legacy: {
      legacy_fixed_effective_rain_assumption:
        "This calculation used the previous project assumption of a fixed effective-rainfall fraction. That assumption is no longer applied and this historical row was not rewritten.",
      legacy_effective_rain_policy_unknown:
        "This calculation predates the rainfall/runoff policy contract, so the rainfall entering the balance has no recorded provenance.",
    },
    runoffStatus: {
      measured: "measured",
      modelled: "modelled",
      assumed_zero: "zero (no rainfall recorded)",
      unknown: "not modelled",
    },
    effectiveStatus: {
      derived: "derived",
      modelled: "modelled",
      same_as_precipitation: "equal to observed rainfall",
      unknown: "not known",
    },
    methodValue: {
      direct_precipitation: "observed rainfall used directly",
      measured_runoff: "measured runoff",
      modelled_runoff: "modelled runoff",
      effective_rain_policy: "effective-rainfall policy",
      unknown: "not available",
    },
    quality: {
      high: "high confidence",
      medium: "medium confidence",
      limited: "limited confidence",
      unavailable: "unavailable",
    },
  },
  irrigationEvidence: {
    title: "Applied irrigation",
    amount: "Amount",
    occurrence: "Irrigation happened",
    yes: "confirmed",
    notConfirmed: "not confirmed",
    unknownAmount: "unknown",
    recordedAs: "Recorded as",
    safeNextStep: "Safe next step",
    unknown: {
      title: "Applied irrigation provenance not recorded",
      body: "This calculation predates the applied-irrigation evidence contract, so the origin of the irrigation depth was not recorded."
    },
    status: {
      measured: "measured",
      observed: "observed",
      confirmed: "producer-confirmed",
      declared: "producer-declared",
      unknown_amount: "amount unknown",
      confirmed_no_irrigation: "no irrigation confirmed",
      planned: "planned only",
      missing: "not recorded",
      invalid: "not usable"
    },
    source: {
      irrigation_event: "irrigation log",
      producer_declaration: "producer declaration",
      flow_meter: "flow meter",
      controller: "irrigation controller",
      farmops_confirmation: "producer confirmation",
      field_activity: "field notebook",
      none: "no source"
    },
    quality: {
      high: "high quality",
      medium: "medium quality",
      limited: "limited quality",
      unavailable: "unavailable"
    }
  },
  rootZoneDepletion: {
    title: "Root-zone soil-water state",
    depletion: "Depletion",
    capacity: "Capacity",
    from: "Carried from",
    rejected: "State not used",
    safeNextStep: "Safe next step",
    status: {
      propagated: "Carried forward",
      initialized: "Initialized from evidence",
      limited_initialization: "Limited initialization",
      missing: "No state available",
      invalid: "State not usable",
      blocked: "Blocked",
    },
    source: {
      previous_dual_kc_state: "Previous daily state",
      measured_soil_moisture: "Measured soil moisture",
      water_balance_initialization: "Water balance initialization",
      producer_declared_state: "Producer declared",
      none: "No source",
    },
    quality: {
      high: "High confidence",
      medium: "Medium confidence",
      limited: "Limited",
      unavailable: "Unavailable",
    },
    reason: {
      previous_state_missing: "no previous daily state exists",
      previous_state_invalid: "the previous state is incomplete or out of bounds",
      previous_state_legacy_untrusted: "the previous record has no verifiable provenance",
      taw_incompatible: "the soil-water capacity changed materially",
      temporal_gap: "there is a gap in the daily state chain",
      previous_state_read_failed: "the previous state could not be read",
      previous_state_blocked: "the previous state was blocked",
      invalid_taw_or_raw: "the root-zone storage values are unusable",
    },
    unknown: {
      title: "Soil-water state provenance not recorded",
      body: "This calculation predates the soil-water state contract, so the origin of the root-zone depletion was not recorded.",
    },
  },
  gauchoWaterTimeline: {
    title: "Water balance over time",
    coverage: "{{computed}}/{{total}} days with state",
    latest: "Latest state",
    depleted: "depleted",
    unknown: "Not available",
    continuousDays: "Continuous chain",
    days: "{{count}} days",
    noChain: "No continuous chain",
    trend: "Trend",
    gaps: "Missing days",
    notReadable:
      "Not enough continuous daily state to read a water-stress trend. Missing days are shown as gaps and are never estimated.",
    disclaimer:
      "Read-only history of already-calculated state. Gaps are not interpolated and no irrigation is executed.",
    status: {
      computed: "Calculated",
      legacy: "Legacy record",
      blocked: "Blocked by missing evidence",
      gap: "No state recorded",
    },
    trendValue: {
      increasing_depletion: "Depletion increasing",
      decreasing_depletion: "Depletion decreasing",
      stable: "Stable",
      unknown: "Not readable yet",
    },
  },
  gauchoTaw: {
    title: "Root-zone water capacity",
    taw: "Root-zone water capacity",
    source: "Source",
    rootDepth: "Root depth",
    quality: "Evidence quality",
    technicalDetails: "Technical details",
    sourceRecord: "Source record",
    distance: "Distance",
    depth: "Sampling depth",
    fcwp: "FC / WP",
    contract: "Contract",
    quality_high: "High",
    quality_medium: "Medium",
    quality_limited: "Limited",
    quality_unavailable: "Not recorded",
    sourceName: {
      producer_lab: "Producer laboratory analysis",
      producer_field: "Producer field sample",
      lucas: "LUCAS soil reference",
      soilgrids: "SoilGrids modelled reference",
      none: "Not available"
    },
    summary: {
      producer_lab: "Based on the parcel soil analysis, the root-zone available water capacity is approximately {{taw}} mm.",
      producer_field: "Based on the producer field sample declared for this parcel, the root-zone available water capacity is approximately {{taw}} mm.",
      lucas: "SentinelCrop is using a LUCAS soil reference for this parcel; the model estimates root-zone available water capacity at approximately {{taw}} mm.",
      soilgrids: "SentinelCrop is using a SoilGrids modelled soil reference for this parcel; the model estimates root-zone available water capacity at approximately {{taw}} mm.",
      blocked: "The root-zone water capacity could not be established from the available evidence.",
      invalid: "The recorded root-zone water capacity does not match the current parcel context, so it is not used as current evidence.",
      legacy: "This record predates the soil-water provenance contract, so its origin is unavailable (legacy record)."
    }
  },
  tawProvenance: {
    title: "Soil-water capacity source",
    referenceOnly: "Reference only",
    modelled: "Modelled",
    legacy: "Legacy record",
    taw: "TAW",
    rootDepth: "Root depth",
    rootDepthReference: "reference",
    depth: "Sampling depth",
    distance: "Source distance",
    source: {
      producerLab: "Producer lab analysis",
      producerField: "Producer field sample",
      lucas: "LUCAS reference sample",
      soilgrids: "SoilGrids modelled reference"
    },
    unavailable: {
      title: "Soil-water capacity source not recorded",
      body: "This calculation has no verifiable soil-water capacity provenance, so it is kept for audit only."
    }
  },
  kcbClimate: {
    title: "Crop coefficient climate adjustment",
    unavailable: "unavailable",
    applied: "Local climate correction applied (FAO-56 Eq. 70 / Eq. 72).",
    notApplied: "FAO-56 reference coefficient used without local climate adjustment.",
    baseKcb: "Reference Kcb",
    selectedKcb: "Kcb used",
    kcMax: "Kc max used",
    etoMethod: "ETo method",
    rhmin: "RHmin",
    u2: "Wind at 2 m",
    height: "Crop height",
    stage: "Stage",
    unknown: {
      title: "Crop coefficient climate provenance not recorded",
    },
    legacy: {
      legacy_climate_adjustment_assumption: "This calculation used the historical RH = 60 % / u2 = 2 m/s assumption; it was not evidence-backed.",
      legacy_climate_adjustment_provenance_unknown: "This calculation predates the climate-adjustment contract, so it is not known whether a local climate correction was applied.",
    },
    status: {
      adjusted: "Climate adjusted",
      tabulated_unadjusted: "Reference value",
      limited: "Limited",
      missing: "Missing",
      invalid: "Invalid",
      blocked: "Blocked",
    },
    quality: { high: "High", medium: "Medium", limited: "Limited", unavailable: "Unavailable" },
    eto: { penman_monteith: "Penman-Monteith", hargreaves_samani: "Hargreaves-Samani", unknown: "Unknown" },
    stageName: { initial: "Initial", development: "Development", mid_season: "Mid-season", late_season: "Late season", unknown: "Unknown" },
    limitation: {
      rhmin_unavailable_no_humidity_evidence: "No humidity evidence is available for this date.",
      rhmin_unavailable_humidity_is_rhmean: "Only mean relative humidity is available; FAO-56 requires the minimum.",
      rhmin_unavailable_humidity_is_rhmax: "Only maximum relative humidity is available; FAO-56 requires the minimum.",
      rhmin_unavailable_humidity_is_unspecified: "The humidity variable type is not declared, so it cannot be used as RHmin.",
      rhmin_invalid_out_of_range: "The reported minimum humidity is outside the valid range.",
      u2_unavailable_no_wind_evidence: "No wind evidence is available for this date.",
      u2_unavailable_wind_height_unknown: "The wind measurement height is unknown, so no 2 m wind speed can be used.",
      u2_invalid_out_of_range: "The reported wind speed is outside the valid range.",
      crop_height_unavailable_for_climate_adjustment: "No crop height evidence is available.",
      kcb_not_climate_adjusted_to_local_conditions: "Kcb is the FAO-56 reference value, not adjusted to local climate.",
      kcmax_not_climate_adjusted_to_local_conditions: "Kc max uses the FAO-56 reference climate form max(1.2, Kcb + 0.05).",
      kcb_below_045_not_climate_corrected_per_fao56: "FAO-56 does not correct Kcb values at or below 0.45.",
      kcb_initial_stage_not_climate_corrected_per_fao56: "FAO-56 does not climate-correct the initial-stage Kcb.",
      base_kcb_invalid: "No valid reference Kcb could be established.",
    },
  },
  evidenceBlocker: {
    heading: "Water balance needs more information",
    technicalDetails: "Technical details",
    codeLabel: "Canonical code",
    gate: "Gate",
    contract: "Evidence contract",
    date: "Calculation date",
    httpStatus: "Status",
    savedNote: "Evidence saved. The next calculation will use it; nothing is recalculated automatically.",
    severity: {
      blocking: "Blocking",
      limited: "Limited evidence",
      warning: "Incomplete context",
      system_error: "Technical issue",
    },
    action: {
      confirm_full_wetting: "Confirm soil surface wetting",
      declare_root_zone_depletion: "Provide initial root-zone reference",
      record_irrigation: "Add irrigation amount",
      confirm_no_irrigation: "Confirm irrigation status",
      confirm_crop: "Confirm the crop",
      enter_crop_height: "Add crop height",
      declare_wetted_fraction: "Provide wetted soil fraction",
      update_irrigation_system: "Confirm the parcel irrigation system",
      wait_for_weather: "Wait for weather evidence",
      retry_later: "Try again later",
      contact_support: "Contact support",
      none: "No action needed",
    },
    code: {
      blocked_missing_wetted_fraction_evidence: {
        title: "The wetted soil fraction is not documented",
        body: "FAO-56 needs the fraction of soil surface wetted by irrigation (fw). No value is assumed from the irrigation system type, so the dual Kc calculation does not run until measured or declared evidence exists.",
      },
      blocked_missing_irrigation_system_evidence: {
        title: "The irrigation system of this parcel is unknown",
        body: "Without a documented irrigation system there is no basis for the wetted soil fraction. An unknown system is never read as sprinkler.",
      },
      blocked_invalid_wetted_fraction_evidence: {
        title: "The recorded wetted soil fraction is not valid",
        body: "The stored wetted fraction is outside the physical range (0 to 1) or is not a number, so it is not used.",
      },
      blocked_conflicting_wetted_fraction_evidence: {
        title: "Conflicting wetted soil fraction records",
        body: "Several records of equal rank report different wetted fractions for this date. The conflict must be resolved before the calculation runs.",
      },
      blocked_missing_soil_evaporation_state: {
        title: "Water balance needs an initial surface-water reference",
        body: "There is no starting reference for the surface soil layer. If the soil surface was fully wetted today (heavy rain or full irrigation), you can confirm it to initialize the model.",
      },
      blocked_missing_root_zone_depletion: {
        title: "Root-zone water status needs an initial reference",
        body: "The root-zone water balance cannot start without a first trustworthy reference. Only approved options are offered; nothing is assumed for you.",
      },
      blocked_missing_irrigation_amount: {
        title: "Irrigation evidence is incomplete for this date",
        body: "The water balance does not yet have trustworthy evidence of the irrigation amount applied on this date. A missing amount is never treated as zero.",
      },
      blocked_missing_irrigation_evidence: {
        title: "Irrigation for this date is unknown",
        body: "SentinelCrop cannot determine whether irrigation occurred on this date. You can record what happened, or confirm that no irrigation took place.",
      },
      blocked_missing_crop_height_evidence: {
        title: "Crop height is needed for the local climate adjustment",
        body: "No measured, declared or reference crop height is available for this crop and stage.",
      },
      blocked_missing_depletion_fraction: {
        title: "No FAO-56 water-stress reference for this crop",
        body: "There is no supported FAO-56 reference for this crop. Confirming the crop identity may resolve it.",
      },
      blocked_missing_root_depth: {
        title: "No FAO-56 rooting-depth reference for this crop",
        body: "The crop has no supported rooting-depth reference. Confirming the crop identity may resolve it.",
      },
      blocked_missing_crop_kc_reference: {
        title: "No FAO-56 crop coefficient curve for this crop",
        body: "This crop type does not match an approved FAO-56 Kc curve, so no crop coefficient was assumed. Confirming the crop identity on the parcel may resolve it.",
      },
      blocked_missing_phenology_evidence: {
        title: "Crop stage is unknown",
        body: "No observed crop stage is available for this date, so stage-dependent coefficients cannot be selected.",
      },
      blocked_invalid_phenology_evidence: {
        title: "Recorded crop dates are not usable",
        body: "The recorded planting or crop dates are inconsistent for this date, so the crop stage cannot be established. Confirming the crop and its dates may resolve it.",
      },
      blocked_invalid_evidence: {
        title: "Soil water evidence is not usable",
        body: "Soil evidence exists for this parcel but does not pass the water-capacity checks. This is a data issue, not a field observation you need to provide.",
      },
      blocked_implausible_et0: {
        title: "Reference evapotranspiration is outside the plausible range",
        body: "The evapotranspiration calculated from the available weather evidence is not plausible, so no water balance was produced. The raw value was preserved and not capped.",
      },
      blocked_et0_review_required: {
        title: "Reference evapotranspiration needs review",
        body: "The evapotranspiration calculated for this date is extreme and is held for review before it can be used. No field information is missing on your side.",
      },
      blocked_missing_taw: {
        title: "Root-zone water capacity could not be established",
        body: "The available soil evidence is not sufficient to establish the root-zone water capacity. This depends on soil reference data, not on information you can enter in the field.",
      },
      invalid_taw: {
        title: "Root-zone water capacity is not usable",
        body: "The available soil evidence produced an unusable water-capacity value. This is a data issue, not a field observation you need to provide.",
      },
      blocked_missing_soil_texture_evidence: {
        title: "Soil texture evidence is unavailable",
        body: "No source-backed soil texture is available for this parcel, so soil water parameters cannot be derived.",
      },
      blocked_missing_et0: {
        title: "Reference evapotranspiration is unavailable",
        body: "No valid reference evapotranspiration is available for this date. This resolves itself once weather evidence is published.",
      },
      blocked_missing_et0_inputs: {
        title: "Weather evidence is incomplete",
        body: "Some weather variables required to compute reference evapotranspiration are unavailable for this date.",
      },
      blocked_missing_climate_inputs: {
        title: "Weather evidence is unavailable",
        body: "No provider delivered usable weather evidence for this date and location.",
      },
      blocked_missing_precipitation_evidence: {
        title: "Rainfall evidence is unavailable",
        body: "Rainfall for this date is unknown, and unknown rainfall is never treated as zero.",
      },
      blocked_missing_rain_water_evidence: {
        title: "Rain water balance evidence is unavailable",
        body: "The rainfall contribution to the water balance cannot be established for this date.",
      },
      blocked_missing_kcb_climate_policy: {
        title: "Crop coefficient policy could not be resolved",
        body: "The crop coefficient policy could not be resolved for this date. This is handled by the platform.",
      },
      limited_kcb_tabulated_unadjusted: {
        title: "Using FAO-56 reference crop coefficients",
        body: "Local climate adjustment is unavailable because minimum humidity or wind evidence is missing. The calculation proceeds with tabulated reference values.",
      },
      limited_crop_height_reference: {
        title: "Crop height uses an FAO-56 reference value",
        body: "The height used is a reference value, not a field measurement.",
      },
      limited_declared_root_zone_depletion: {
        title: "Root-zone depletion was declared by the producer",
        body: "The starting reference is a declaration, so evidence quality is limited compared with a measurement.",
      },
      limited_runoff_unknown: {
        title: "Runoff is unknown",
        body: "Runoff could not be established, so effective rainfall is reported without a runoff deduction.",
      },
      limited_weather_fallback_provider: {
        title: "Weather evidence comes from a fallback provider",
        body: "The preferred provider was unavailable; canonical weather evidence remains valid.",
      },
      system_error: {
        title: "SentinelCrop could not complete this calculation",
        body: "A technical error prevented the calculation. No field information is missing on your side.",
      },
    },
  },
  irrigationWaterState: {
    title: "Canonical soil-water state (FAO-56 Dual Kc)",
    unavailable: "Not available",
    noCanonicalRow: "No canonical FAO-56 Dual Kc water-balance result exists for this parcel and date.",
    systemError: "The canonical water-balance state could not be read.",
    etcLabel: "Crop evapotranspiration (ETc)",
    etcEquivalent: "Equivalent to ~{{value}} m³/ha/day of modelled crop water use",
    drLabel: "Root-zone depletion (Dr)",
    drEquivalent: "Storage-gap equivalent: ~{{value}} m³/ha",
    rawLabel: "Readily available water (RAW)",
    tawLabel: "Total available water (TAW)",
    ksLabel: "Stress coefficient (Ks)",
    statusLabel: "Management threshold",
    thresholdNotReached: "Dr is below RAW — the management allowed depletion has not been reached",
    thresholdReached: "Dr has reached or exceeded RAW — the management allowed depletion is reached",
    stressActive: "Canonical water stress active (Ks < 1)",
    stressInactive: "No canonical water stress (Ks = 1)",
    applicationTargetLabel: "Irrigation application actions",
    applicationTargetBlocked: "Legacy save and field-registration actions remain disabled. ETc and Dr are not application amounts. Any supervised canonical NET target is shown separately and remains subject to its evidence gates.",
  },
  canonicalNetTarget: {
    captureForFieldValidation: "Capture for field validation",
    capturedForFieldValidation: "Captured for field validation",
    captureError: "This decision could not be captured. Nothing was stored.",
    captureMeaning: "Captures this decision snapshot for prospective field validation. It does not mean the decision is agronomically validated or field verified.",
    title: "Canonical net irrigation target (supervised)",
    reviewRequiredBadge: "Human review required",
    netLabel: "Supervised NET root-zone water target",
    isNetRootZone: "This is a NET water target at the root-zone layer.",
    basedOnEvidence: "Based on the canonical modelled FAO-56 Dual Kc water state plus a reviewed management policy.",
    notGross: "It is NOT a gross applied depth: no irrigation-system efficiency is applied.",
    notDuration: "It is NOT an irrigation duration or run time.",
    notSchedule: "It is NOT a schedule.",
    humanReviewRequired: "Human agronomic review is required before any decision.",
    notExecutionCommand: "It is not an execution command and SentinelCrop performs no irrigation action.",
    notTriggeredTitle: "Management threshold not reached",
    notTriggeredBody: "No irrigation target is proposed.",
    notAvailable: "Not available",
    safeNextStep: "Safe next step: recompute the canonical water balance and confirm the reviewed target policy and post-state water evidence. No numeric target is shown while the chain is blocked.",
    blockers: {
      unknown: "The canonical net target chain is blocked by an unresolved evidence gate.",
      system_error: "A technical error prevented reading the canonical evidence.",
      missing_canonical_water_state: "No canonical FAO-56 Dual Kc water state exists for this parcel and day.",
      rejected_legacy_water_state: "The persisted water-balance row is legacy or non-eligible evidence.",
      invalid_decision_day: "The decision day is not a valid UTC calendar day.",
      no_active_crop_season: "No active crop season exists for this parcel.",
      ambiguous_active_crop_season: "More than one active crop season exists; selection fails closed.",
      missing_reviewed_policy: "No reviewed irrigation target-depletion policy exists for this crop season.",
      ambiguous_active_policy: "More than one active target policy exists; selection fails closed.",
      invalid_policy_record: "The target policy record is structurally invalid.",
      policy_not_yet_valid: "The reviewed target policy is not yet valid on this day.",
      policy_expired: "The reviewed target policy has expired.",
      exact_water_state_cutoff_unavailable: "The water balance provides no exact evidence cutoff, so later rain or irrigation cannot be ruled out.",
      temporal_resolution_insufficient: "The available temporal resolution cannot order water events against the canonical state.",
      post_state_water_event_requires_recompute: "Rain or irrigation occurred after the canonical state; the water balance must be recomputed.",
      post_state_water_event_evidence_incomplete: "Post-state rain or irrigation evidence is incomplete; missing evidence is never read as no event.",
      stale_water_state: "The canonical water state is not current-day.",
      missing_post_state_water_event_evidence: "Post-state rain or irrigation evidence is missing.",
      missing_root_zone_depletion: "The current root-zone depletion (Dr) is unavailable.",
      invalid_root_zone_depletion: "The current root-zone depletion (Dr) is not valid.",
      management_threshold_unavailable: "The canonical management-threshold state is unavailable.",
      management_threshold_not_reached: "The management allowed depletion has not been reached.",
      missing_taw_for_target_policy_validation: "Total available water (TAW) is unavailable, so the policy cannot be validated.",
      invalid_taw_for_target_policy_validation: "Total available water (TAW) is not valid.",
      missing_target_policy: "No reviewed target-depletion policy was supplied.",
      invalid_target_policy: "The reviewed target-depletion policy is not valid for this state.",
    },
  },
  /** FIELD-VALIDATION-PILOT-WORKFLOW-1 — prospective validation workflow. */
  fieldValidation: {
    title: "Prospective field validation",
    prospectiveBadge: "Pilot — prospective, not validated",
    disclaimer:
      "This records what was known at decision time, what a human decided and what actually happened. It is not a validated result, an accuracy claim or a performance metric.",
    snapshotOnly:
      "Shown from the frozen decision snapshot. It is never recomputed with newer data.",
    loading: "Loading captured decisions…",
    noEpisodes: "No decision has been captured for this parcel yet.",
    netTarget: "Net application target",
    storageGap: "Storage-gap equivalent (Dr)",
    targetDepletion: "Reviewed target depletion",
    blocker: "Blocker",
    none: "None",
    notAvailable: "Not available",
    states: {
      OPEN: "Open",
      DECISION_CAPTURED: "Decision captured",
      HUMAN_REVIEWED: "Human reviewed",
      OUTCOME_PENDING: "Outcome pending",
      OUTCOME_CAPTURED: "Outcome captured",
      CLOSED: "Closed",
    },
    b3Status: {
      available: "Target available",
      not_triggered: "Not triggered",
      blocked: "Blocked",
      system_error: "System error",
    },
    reviewTitle: "Human review",
    recordReview: "Record human review",
    reviewNotesPlaceholder: "Optional notes on this decision",
    reviewerIdentityNote:
      "The reviewer identity is recorded by the database from your signed-in account. It cannot be chosen here.",
    reviewStatus: {
      agree: "I agree with the decision",
      partially_agree: "I partially agree",
      disagree: "I disagree",
      not_reviewed: "Not reviewed",
    },
    reviewerRole: {
      farmer: "Farmer",
      agronomist: "Agronomist",
      advisor: "Advisor",
      internal_test: "Internal test",
    },
    actionTitle: "Actual field action",
    recordAction: "Record actual action",
    linkIrrigationEvent: "Link a real irrigation event (optional)",
    linkFarmOps: "Link a real field confirmation (optional)",
    noLinkedRecord: "No linked record",
    declaredAmount: "Water actually applied (mm)",
    declaredAmountPlaceholder: "mm actually applied",
    declaredAmountNote:
      "This is what YOU applied. It is never a recommendation and is never used to compute one.",
    declaredDay: "Day the action actually happened",
    occurrenceNote:
      "Occurrence time comes only from a linked record or from this declaration. It is never taken from a row creation timestamp.",
    actionKind: {
      no_action: "No action taken",
      irrigation_applied: "Irrigation applied",
      irrigation_applied_unknown_depth: "Irrigation applied, depth unknown",
      different_irrigation_amount: "Irrigated a different amount",
      postponed: "Postponed",
      other: "Other",
    },
    outcomeTitle: "Follow-up evidence",
    recordOutcome: "Record selected evidence",
    noOutcomeAvailable: "No follow-up evidence available",
    noFollowUpCandidates:
      "No follow-up record exists after this decision. You can still mark that no evidence is available.",
    provenanceNote:
      "Provenance is derived from the source record. A declared observation is never presented as a measurement.",
    outcomeAvailability: {
      captured: "Captured",
      none_available: "None available",
      pending: "Pending",
    },
    sourceType: {
      soil_moisture_observations: "Soil moisture observation",
      ground_truth_measurements: "Ground-truth measurement",
      scouting_reports: "Scouting report",
      gaucho_action_observation: "Field observation",
      iot_readings: "Sensor reading",
      index_time_series: "Satellite index",
      advanced_satellite_indices: "Satellite index",
      fao56_dual_kc_results: "Model result",
      farmops_confirmations: "Field confirmation",
    },
    provenance: {
      FIELD_MEASURED: "Field measured",
      FIELD_OBSERVED: "Field observed",
      SENSOR_MEASURED: "Sensor measured",
      SATELLITE_OBSERVED: "Satellite observed",
      MODEL_DERIVED: "Model derived",
      USER_DECLARED: "User declared",
    },
    closeNote: "Closing adds no new fact. The episode becomes permanently read-only.",
    closeEpisode: "Close episode",
    readinessTitle: "Pilot readiness",
    readinessUnknownNote:
      "An unknown condition is never counted as satisfied. Nothing here is inferred.",
    readinessStatus: {
      ready: "Ready to operate",
      not_ready: "Not ready",
      satisfied: "Satisfied",
      not_satisfied: "Missing",
      unknown: "Unknown",
    },
    readinessCheck: {
      parcel_geometry: "Parcel boundary recorded",
      crop_identified: "Crop identified",
      active_crop_season: "Exactly one active crop season",
      season_start_date: "Season start date recorded",
      irrigation_system_declared: "Irrigation system declared",
      soil_water_holding_inputs: "Measured soil water-holding inputs",
      root_depth_input: "Rooting depth declared",
      reviewed_target_policy: "Reviewed target-depletion policy",
      water_state_evidence_available: "Canonical water-state evidence exists",
      named_reviewer: "A named reviewer has been recorded",
      owner_data_access_consent: "Owner consent to use this data in the pilot",
    },
    errors: {
      unknown_error: "The operation could not be completed.",
      not_authenticated: "Sign in to operate field validation.",
      episode_read_failed: "The captured decisions could not be read.",
      episode_not_found: "This episode is not visible to your account.",
      lifecycle_state_conflict: "This episode already advanced. Reload and try again.",
      lifecycle_transition_rejected: "The database rejected this transition.",
    },
  },
};
